import { Fragment, useEffect, useState } from "react";
import ArrowSquareUpIcon from "@icons/arrow-square-up.svg?react";
import XIcon from "@icons/x-close.svg?react";
import { Transition, Dialog, TransitionChild, DialogPanel, DialogTitle } from "@headlessui/react";
import { Divider } from "@mui/material";
import DragNDrop from "./DragnDrop";
import Matcher from "./Matcher";
import { DOCUMENT_STORAGE_BUCKET } from "@/common/utils/constants";
import { useAccountContext } from "@/state/providers/AccountProvider";
import ImportResults from "./ImportResults";
import ArrowLeftIcon from "@icons/arrow-left.svg?react";
import { FeatureFlags, ImportFilePayload, ImportFileResponse } from "@/common/utils/types";
import { importAppointmentsFile } from "@/common/lib/api";
import { toast } from "react-toastify";
import { removeFromStorage } from "@/common/lib/supabaseClient";
import Spinner from "../Spinner";
import Papa from "papaparse";
import { useDataContextApi } from "@/state/providers/DataProvider";
import ConfirmModal from "../ConfirmModal";
import { DateTime } from "luxon";

const VISIT_ID_NAME = "Visit Id";

// this type defines our configuration for mapping display labels to payload keys.
export type FieldMapping = {
    /** The text to display to the user. */
    displayLabel: string;
    /** The key to set on the ImportFilePayload. */
    key: keyof ImportFilePayload;
    /** Indicates if this mapping is required. */
    required: boolean;
};

// in the future we should fetch this mapping then it can be dynamic based on what pims the user is importing from
export const headerMappingConfig: FieldMapping[] = [
    { displayLabel: "Visit Id", key: "visitIdColumn", required: true },
    { displayLabel: "Visit Scheduled At", key: "visitScheduledAtColumn", required: true },
    { displayLabel: "Visit Appointment Duration", key: "visitAppointmentDurationColumn", required: true },
    { displayLabel: "Patient Id", key: "patientsIdColumn", required: true },
    { displayLabel: "Patient Name", key: "patientsNameColumn", required: true },

    { displayLabel: "Visit Doctor", key: "visitDoctorColumn", required: false },
    { displayLabel: "Visit Type", key: "visitTypeColumn", required: false },
    { displayLabel: "Patient Sex", key: "patientsSexColumn", required: false },
    { displayLabel: "Patient Breed", key: "patientsBreedColumn", required: false },
    { displayLabel: "Patient Dob", key: "patientsDobColumn", required: false },
    { displayLabel: "Patient Species", key: "patientsSpeciesColumn", required: false },
    { displayLabel: "Contact Id", key: "contactsIdColumn", required: false },
    { displayLabel: "Contact First Name", key: "contactsFirstNameColumn", required: false },
    { displayLabel: "Contact Last Name", key: "contactsLastNameColumn", required: false },
    { displayLabel: "Contact Primary Phone", key: "contactsPrimaryPhoneColumn", required: false },
    { displayLabel: "Contact Primary Email", key: "contactsPrimaryEmailColumn", required: false },
];

interface Props {
    disabled?: boolean;
    useConfirmModal?: boolean;
}

/**
 * remove example row from the parsed CSV data
 * @param parsed the parsed CSV data
 * @returns the parsed CSV data with the example row removed
 */
const removeExampleRow = (parsed: Papa.ParseResult<unknown>) => {
    if (parsed.data && parsed.data.length > 0) {
        const exampleRowCondition = (row: any) =>
            row["Patient Id"] === "01984" && row["Patient Name"] === "ExamplePatient";
        const exampleRow = parsed.data.find((row) => exampleRowCondition(row));
        if (exampleRow) {
            parsed.data = parsed.data.filter((row) => !exampleRowCondition(row));
        }
    }

    return parsed;
};

/**
 * Remove rows with ALL empty strings
 * @param parsed the parsed CSV data
 * @returns the parsed CSV data with rows with empty strings removed
 */
const cleanRows = (parsed: Papa.ParseResult<unknown>) => {
    // make sure there are no rows with all empty strings
    parsed.data = parsed.data.filter((row: any) => Object.values(row).some((value: any) => value !== ""));

    return parsed;
};

const generateVisitId = (row: any) => {
    try {
        const patientName = row["Patient Name"];
        const visitScheduledAt = new Date(row["Visit Scheduled At"]).toISOString().split("T")[0];
        return `${patientName}_${visitScheduledAt}`;
    } catch {
        return "failed to generate";
    }
};

/**
 * If the csv doesn't include visit id, then add the visit id to the headers
 * and set each data point to the patient name and date w/o time stamp
 */
const handlePossibleMissingVisitIds = (parsed: Papa.ParseResult<any>) => {
    // handle the case where the visit id column doesn't exist
    if (!parsed.meta.fields?.some((header) => header === VISIT_ID_NAME)) {
        parsed.meta.fields?.push(VISIT_ID_NAME);
        parsed.data.forEach((row: any) => {
            row[VISIT_ID_NAME] = generateVisitId(row);
        });
    }
    // handle the case where the visit id column exists but it's empty for the rows
    else if (
        parsed.meta.fields?.some((header) => header === VISIT_ID_NAME) &&
        parsed.data.some((row: any) => row[VISIT_ID_NAME] === "")
    ) {
        parsed.data.forEach((row: any) => {
            if (!row[VISIT_ID_NAME]) {
                row[VISIT_ID_NAME] = generateVisitId(row);
            }
        });
    }
    return parsed;
};

/**
 * Prepare the CSV data for import from the original file
 */
export const prepareCsvFromFile = async (file: File) => {
    const csvText = await file.text();

    let parsed = Papa.parse(csvText, { header: true });

    parsed = removeExampleRow(parsed);
    parsed = cleanRows(parsed);
    parsed = handlePossibleMissingVisitIds(parsed);

    return parsed;
};

const ImportModal: React.FC<Props> = ({ disabled = false, useConfirmModal = false }) => {
    const { accountData, pmsIntegrationData, fileIntegrationEnabled, containsFeatureFlag } = useAccountContext();
    const { refreshPatientRecords, selectedDate } = useDataContextApi();

    const [isOpen, setIsOpen] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [file, setFile] = useState<File | null>(null);
    const [mapping, setMapping] = useState<Partial<Record<keyof ImportFilePayload, number>>>({});
    const [mappingResults, setMappingResults] = useState<ImportFileResponse | null>(null);
    const [parsedCsv, setParsedCsv] = useState<Papa.ParseResult<unknown> | undefined>();

    const handleNext = () => {
        setStep(step + 1);
    };

    const handleBack = () => {
        if (step === 1) {
            setIsOpen(false);
            return;
        }

        setStep(step - 1);
    };

    const clearState = () => {
        setStep(1);
        setFile(null);
        setMapping({});
        setMappingResults(null);
        setParsedCsv(undefined);
    };

    const closeModel = () => {
        clearState();
        setIsOpen(false);
    };

    const handleConfirmModalConfirm = () => {
        setIsConfirmOpen(false);
        setIsOpen(true);
    };

    const handleConfirmModalCancel = () => {
        setIsConfirmOpen(false);
    };

    // check if all required fields are mapped
    const areRequiredFieldsMapped = () => {
        return headerMappingConfig
            .filter((config) => config.required)
            .every((config) => mapping[config.key] !== undefined);
    };

    /**
     * Remove file from storage and reset state
     */
    const removeFile = async (file: File) => {
        // remove file from supabase
        if (!file) return;

        const filePath = `${accountData?.id}/data_import/${file?.name}`;

        if (filePath) {
            const { error: removeError } = await removeFromStorage(DOCUMENT_STORAGE_BUCKET, filePath);

            if (removeError) {
                toast.error("Failed to remove file from storage");
                return;
            }

            setFile(null);
            setMapping({});
        }
    };

    /**
     * Handle importing appointments
     * @param process whether to process the appointments after importing
     * @returns
     */
    const handleImportAppointments = async (process: boolean) => {
        // check if file is selected
        if (!file) {
            return;
        }

        setLoading(true);

        // convert the date to a local date
        const localDate = DateTime.fromJSDate(selectedDate?.startDate || new Date());

        if (!localDate.isValid) {
            toast.error(`Invalid date: ${localDate.invalidExplanation}`);
            setLoading(false);
            return;
        }

        // get the client timezone offset in hours
        const clientTimezoneOffsetHours = localDate.offset / 60;

        // create the payload
        const payload: ImportFilePayload = {
            // required fields
            url: `${accountData?.id}/data_import/${file.name}`,
            hasHeaderRow: true,
            timezoneOffset: clientTimezoneOffsetHours,
            visitIdColumn: mapping.visitIdColumn as number,
            visitScheduledAtColumn: mapping.visitScheduledAtColumn as number,
            visitAppointmentDurationColumn: mapping.visitAppointmentDurationColumn as number,
            patientsIdColumn: mapping.patientsIdColumn as number,
            patientsNameColumn: mapping.patientsNameColumn as number,

            // optional fields
            visitDoctorColumn: mapping.visitDoctorColumn,
            visitTypeColumn: mapping.visitTypeColumn,
            patientsSexColumn: mapping.patientsSexColumn,
            patientsBreedColumn: mapping.patientsBreedColumn,
            patientsDobColumn: mapping.patientsDobColumn,
            patientsSpeciesColumn: mapping.patientsSpeciesColumn,
            contactsIdColumn: mapping.contactsIdColumn,
            contactsFirstNameColumn: mapping.contactsFirstNameColumn,
            contactsLastNameColumn: mapping.contactsLastNameColumn,
            contactsPrimaryPhoneColumn: mapping.contactsPrimaryPhoneColumn,
            contactsPrimaryEmailColumn: mapping.contactsPrimaryEmailColumn,
        };

        // import the appointments but don't process them
        const { data, error: importError } = await importAppointmentsFile(payload, process);

        if (importError) {
            toast.error(`Failed to import appointments: ${importError.message}`);
            setLoading(false);
            return;
        }

        // if there are no row failures, process the appointments
        if (!process && data && data.rowFailures.length === 0) {
            const { error: processError } = await importAppointmentsFile(payload, true);

            if (processError) {
                toast.error(`Failed to process appointments: ${processError.message}`);
                setLoading(false);
                return;
            }

            // remove the file from storage
            await removeFile(file);

            refreshPatientRecords();

            setLoading(false);

            // close the modal
            closeModel();

            // show success message
            toast.success("Appointments imported successfully");

            return;
        }

        // if we are processing the appointments, process, then go back to the first step
        if (process && data) {
            // remove the file from storage
            await removeFile(file);

            refreshPatientRecords();

            setLoading(false);

            // close the modal
            closeModel();

            // show success message
            toast.success("Appointments imported successfully");

            return;
        }

        // if there are row failures, and we aren't processing set the mapping results
        setMappingResults(data);

        setLoading(false);

        handleNext();

        return;
    };

    useEffect(() => {
        // get the csv data from the uploaded CSV file
        const getCsvData = async (file: File) => {
            const parsed = await prepareCsvFromFile(file);

            setParsedCsv(parsed);
        };

        if (!file) return;

        getCsvData(file);
    }, [file]);

    // render the step based on the current step
    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <DragNDrop
                        bucket={DOCUMENT_STORAGE_BUCKET}
                        relativePath={"data_import"}
                        fileType={{
                            "text/csv": [".csv"],
                        }}
                        file={file}
                        setFile={setFile}
                        removeFile={removeFile}
                    />
                );
            case 2:
                return <Matcher parsedCsv={parsedCsv} mapping={mapping} setMapping={setMapping} />;
            case 3:
                return <ImportResults parsedCsv={parsedCsv} mappingResults={mappingResults} />;
            default:
                return (
                    <div className="w-full h-full flex items-center justify-center gap-4">
                        <span className="text-max-900">You won.</span>
                    </div>
                );
        }
    };

    // render the action buttons based on the current step
    const renderActions = () => {
        switch (step) {
            case 1:
                return (
                    <div className="w-full px-6  flex justify-end items-center gap-4">
                        <button className="secondary-button max-w-[75px]" onClick={closeModel}>
                            Close
                        </button>
                        <button disabled={!file} className="primary-button max-w-[75px]" onClick={handleNext}>
                            Next
                        </button>
                    </div>
                );
            case 2:
                return (
                    <div className="w-full px-6  flex justify-between items-center gap-4">
                        <button type="button" className="flex items-center gap-2 cursor-pointer" onClick={handleBack}>
                            <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
                            <span className="text-gray-700 text-sm font-semibold">{"Upload"}</span>
                        </button>
                        <div className="w-1/2 flex items-center gap-4">
                            <button className="secondary-button max-w-[75px]" onClick={closeModel}>
                                Close
                            </button>
                            <button
                                disabled={!areRequiredFieldsMapped()}
                                className="primary-button"
                                onClick={() => handleImportAppointments(false)}
                            >
                                {loading ? (
                                    <Spinner color="text-white" size="xsmall" />
                                ) : (
                                    <ArrowSquareUpIcon className="h-5 h-5" />
                                )}
                                <span className="hidden sm:inline">Import Appointments</span>
                            </button>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="w-full px-6  flex justify-between items-center gap-4">
                        <button type="button" className="flex items-center gap-2 cursor-pointer" onClick={handleBack}>
                            <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
                            <span className="text-gray-700 text-sm font-semibold">{"Back"}</span>
                        </button>
                        <div className="w-1/3 flex items-center gap-4">
                            <button className="secondary-button max-w-[75px]" onClick={closeModel}>
                                Close
                            </button>
                            <button
                                disabled={!file}
                                className="primary-button"
                                onClick={() => handleImportAppointments(true)}
                            >
                                {loading && <Spinner color="text-white" size="xsmall" />}
                                Complete
                            </button>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="flex items-center gap-4">
                        <button className="secondary-button max-w-[75px]" onClick={closeModel}>
                            Close
                        </button>
                    </div>
                );
        }
    };

    if (
        !accountData ||
        !pmsIntegrationData ||
        !fileIntegrationEnabled() ||
        !containsFeatureFlag(FeatureFlags.ENABLE_IMPORT_APPOINTMENTS)
    )
        return null;

    return (
        <Fragment>
            {/* button that opens the modal */}
            <div className="flex shrink-0 min-h-[36px]">
                <button
                    id="import-modal"
                    onClick={() => (useConfirmModal ? setIsConfirmOpen(true) : setIsOpen(true))}
                    disabled={disabled}
                    className={"secondary-button"}
                    // The label is hidden below md, and the icon carries no text of its own, so
                    // without these the button announces as "button" at every phone and tablet
                    // width. Sync and Add Visit beside it already carry the same pair.
                    title="Import"
                    aria-label="Import"
                >
                    <div className="flex flex-row gap-2 items-center justify-center whitespace-nowrap">
                        <span className=" flex items-center justify-center ">
                            <ArrowSquareUpIcon className="h-4 w-4" />
                        </span>
                        <span className="hidden md:inline text-sm text-gray-700 font-semibold">Import</span>
                    </div>
                </button>
                <ConfirmModal
                    id="import-modal-confirm-modal"
                    headerText="Import Appointments"
                    contentText="Appointment import is designed for accounts without a valid integration configured and allows you to import a .csv of appointment details. This will keep your HappyDoc patient list in sync with your Patient Management System."
                    confirmButtonText="OK"
                    cancelButtonText="Cancel"
                    onCancel={handleConfirmModalCancel}
                    onConfirm={handleConfirmModalConfirm}
                    isOpen={isConfirmOpen}
                    confirmButtonColor="bg-max-900"
                    confirmHoverButtonColor="hover:bg-max-800"
                />
            </div>

            {/* modal */}
            <Transition show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(true)}>
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>
                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div className="flex min-h-screen items-center justify-center text-center sm:p-0">
                            <TransitionChild
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <DialogPanel className="relative transform overflow-hidden rounded-md flex flex-col gap-4 bg-white py-6 text-left shadow-sm transition-all m-4 sm:my-8 sm:w-full sm:max-w-2xl">
                                    {/* title div */}
                                    <div className="px-6">
                                        <div className="text-center">
                                            <DialogTitle
                                                as="h1"
                                                className="text-xl mb-3 font-semibold leading-6 text-gray-900 text-left flex flex-row items-center justify-between relative font-"
                                            >
                                                Import Appointments
                                                <button type="button" onClick={closeModel}>
                                                    <XIcon className="h-6 w-6 text-gray-500 hover:text-gray-900 cursor-pointer" />
                                                </button>
                                            </DialogTitle>
                                        </div>
                                        {/* render instructions if the second matching step */}
                                        {step === 2 && (
                                            <div className="text-start text-gray-600 text-sm">
                                                <span>
                                                    Map HappyDoc data to your clinic's data by matching the column
                                                    headers below.
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <Divider />
                                    {/* drag n drop */}
                                    {renderStep()}

                                    <Divider />

                                    {/* action buttons */}
                                    {renderActions()}
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </Fragment>
    );
};

export default ImportModal;
